import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL
} from 'firebase/storage';
import '../styles/AddProduct.css';

const EditProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState({
    name: '',
    price: '',
    discount: '',
    quantity: '',
    description: '',
    category: '',
    images: []
  });

  const [uploadingIndex, setUploadingIndex] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const docRef = doc(db, 'products', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setProduct({
            ...data,
            images: data.images || []
          });
        } else {
          alert('Product not found!');
          navigate('/admin/products');
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      }
    };

    fetchProduct();
  }, [id, navigate]);

  const handleChange = (e) => {
    setProduct({ ...product, [e.target.name]: e.target.value });
  };

  const handleImageUpload = async (e, index = null) => {
    const file = e.target.files[0];
    if (!file) return;

    const storage = getStorage();
    const fileName = `${Date.now()}-${file.name}`;
    const storageRef = ref(storage, `product-images/${id}/${fileName}`);

    try {
      setUploadingIndex(index ?? 'new');
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      setProduct((prev) => {
        const updatedImages = [...prev.images];
        if (index !== null) {
          updatedImages[index] = downloadURL;
        } else {
          updatedImages.push(downloadURL);
        }
        return { ...prev, images: updatedImages };
      });

      alert('✅ Image uploaded successfully!');
    } catch (err) {
      console.error('Image upload failed:', err);
      alert('❌ Image upload failed!');
    } finally {
      setUploadingIndex(null);
    }
  };

  const handleImageRemove = async (index) => {
    const confirmed = window.confirm('Are you sure you want to remove this image?');
    if (!confirmed) return;

    const updatedImages = product.images.filter((_, i) => i !== index);

    setProduct((prev) => ({ ...prev, images: updatedImages }));

    try {
      const docRef = doc(db, 'products', id);
      await updateDoc(docRef, {
        images: updatedImages
      });
      alert('🗑️ Image removed!');
    } catch (err) {
      console.error('Failed to remove image:', err);
      alert('❌ Could not update Firestore after image removal');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const docRef = doc(db, 'products', id);
      await updateDoc(docRef, {
        ...product,
        price: Number(product.price),
        discount: Number(product.discount),
        quantity: Number(product.quantity)
      });

      alert('✅ Product updated successfully!');
      navigate('/admin/products');
    } catch (error) {
      console.error('Error updating product:', error);
      alert('❌ Failed to update product');
    }
  };

  return (
    <div className="add-product-container">
      <h2 className="page-title">Edit Product</h2>
      <form className="add-product-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Product Name</label>
          <input
            type="text"
            name="name"
            value={product.name}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Price (₹)</label>
          <input
            type="number"
            name="price"
            value={product.price}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Discount (%)</label>
          <input
            type="number"
            name="discount"
            value={product.discount}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Quantity</label>
          <input
            type="number"
            name="quantity"
            value={product.quantity}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>Category</label>
          <input
            type="text"
            name="category"
            value={product.category}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            name="description"
            value={product.description}
            onChange={handleChange}
            rows="4"
          />
        </div>

        <div className="form-group">
          <label>Product Images</label>
          {product.images.length > 0 ? (
            product.images.map((imgUrl, index) => (
              <div key={index} style={{ marginBottom: '20px' }}>
                <img
                  src={imgUrl}
                  alt={`Product ${index}`}
                  width="100"
                  style={{ borderRadius: '8px', marginBottom: '5px' }}
                />
                <br />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, index)}
                />
                <button
                  type="button"
                  onClick={() => handleImageRemove(index)}
                  style={{
                    marginLeft: '10px',
                    backgroundColor: '#e74c3c',
                    color: '#fff',
                    border: 'none',
                    padding: '5px 10px',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  Remove
                </button>
                {uploadingIndex === index && <p>Uploading...</p>}
              </div>
            ))
          ) : (
            <p>No images uploaded yet.</p>
          )}

          <div style={{ marginTop: '15px' }}>
            <label>Add New Image:</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e, null)}
            />
            {uploadingIndex === 'new' && <p>Uploading...</p>}
          </div>
        </div>

        <button className="submit-btn" type="submit">
          Update Product
        </button>
      </form>
    </div>
  );
};

export default EditProduct;
